import type { Group } from 'three';
import { createFlipTween, type TweenSet } from './animation.js';
import { type BoardMeshes } from './cells.js';
import { materialFor } from './materials.js';
import type { Board, Coord } from '../core/types.js';

interface TransformSpec {
  readonly rotateY?: number;
  readonly mirror?: 'x' | 'y';
}

export const runBoardTransform = (group: Group, tweens: TweenSet, spec: TransformSpec): void => {
  const dur = 0.7;
  let t = 0;
  const startRotZ = group.rotation.z;
  const targetRotZ = startRotZ + (spec.rotateY ?? 0);
  const startScale = { x: group.scale.x, y: group.scale.y, z: group.scale.z };
  const targetScale = { ...startScale };
  if (spec.mirror === 'x') targetScale.x = -startScale.x;
  if (spec.mirror === 'y') targetScale.y = -startScale.y;

  const ease = (k: number): number => 1 - Math.pow(1 - k, 3);

  tweens.add({
    update(dt) {
      t += dt;
      const k = Math.min(1, t / dur);
      const e = ease(k);
      group.rotation.z = startRotZ + (targetRotZ - startRotZ) * e;
      group.scale.x = startScale.x + (targetScale.x - startScale.x) * e;
      group.scale.y = startScale.y + (targetScale.y - startScale.y) * e;
      return k < 1;
    },
  });
};

const dist = (a: Coord, b: Coord): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Distance-ordered FLIP-OVER ripple from origin outward.
 * Used for inversion / chaos: every cell flips to its new colour as the
 * wave reaches it. This is the cblix "switch-over effect".
 */
export const runColorSweep = (
  meshes: BoardMeshes,
  board: Board,
  origin: Coord,
  tweens: TweenSet,
): void => {
  const speedSec = 0.04;
  for (let y = 0; y < board.size; y++) {
    for (let x = 0; x < board.size; x++) {
      const mesh = meshes.cells[y]![x]!;
      const cell = board.cells[y]![x]!;
      const delay = dist({ x, y }, origin) * speedSec;
      const fromMat = mesh.material as ReturnType<typeof materialFor>;
      const toMat = materialFor(cell.color, cell.tile);
      if (fromMat === toMat) continue;
      tweens.add(createFlipTween(mesh, fromMat, toMat, delay));
    }
  }
};
